import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, Circle, Loader2, Clock } from "lucide-react";

/**
 * Read-only view of the 17-step post-signature workflow for sales-reps.
 *
 * Sales-rep can see EVERY customer-facing milestone (Auftragsbestätigung
 * versendet, AZ bezahlt, Montagebeginn, Inbetriebnahme, BnD an Kunde …)
 * so they can answer customer calls — but they cannot trigger anything
 * here. The dedicated action panels remain manager-/admin-only.
 *
 * Internal-only fields (specific installer, technical handover notes)
 * are NOT exposed by this component on purpose.
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  } catch { return iso; }
};

export default function SalesReadonlyWorkflow({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/back-office/projects/${projectId}/workflow`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return (
      <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="sales-readonly-workflow-loading">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="animate-spin text-zinc-500" size={16} />
        </div>
      </div>
    );
  }
  if (!data || !data.steps?.length) return null;

  const completed = data.completed_count;
  const total = data.total;
  const progress = Math.round((completed / total) * 100);

  return (
    <div className="mt-3 pt-3 border-t border-zinc-800/80 space-y-3" data-testid="sales-readonly-workflow">
      <div>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
            <Clock size={13} className="text-[#00E575]" /> Installations-Status (für Kundenanfragen)
          </div>
          <span className="text-[10.5px] text-zinc-400 font-mono">{completed} / {total}</span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-3">
          <div
            className="h-full bg-[#00E575] transition-all"
            style={{ width: `${progress}%` }}
            data-testid="sales-readonly-progress"
          />
        </div>

        <div className="rounded-lg bg-[#0B0B0D] border border-zinc-800 p-2 space-y-1.5" data-testid="sales-readonly-steps">
          {data.steps.map((s, idx) => {
            const isCurrent = s.key === data.current_step_key;
            return (
              <div
                key={s.key}
                data-testid={`sales-readonly-step-${s.key}`}
                className={`flex items-start gap-2 px-2 py-1.5 rounded ${
                  s.completed ? "bg-transparent" : isCurrent ? "bg-[#00E575]/8" : ""
                }`}
              >
                <div className="flex flex-col items-center pt-0.5 flex-shrink-0">
                  {s.completed
                    ? <CheckCircle2 size={13} className="text-[#00E575]" />
                    : <Circle size={13} className={isCurrent ? "text-[#00E575]" : "text-zinc-600"} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[12px] text-zinc-300 font-mono">{String(idx + 1).padStart(2, "0")}</span>
                    <span className={`text-[12px] ${s.completed ? "text-zinc-300" : isCurrent ? "text-zinc-100 font-semibold" : "text-zinc-300"}`}>
                      {s.label}
                    </span>
                    {isCurrent && !s.completed && (
                      <span className="text-[8px] font-bold uppercase tracking-wider rounded px-1 py-0.5 bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/30">
                        läuft
                      </span>
                    )}
                  </div>
                  {(s.completed_at || s.note) && (
                    <div className="text-[12px] text-zinc-300 mt-0.5 truncate">
                      {s.completed_at && <span>{fmtDate(s.completed_at)}</span>}
                      {s.completed_at && s.note && <span> · </span>}
                      {s.note && <span>{s.note}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[12px] text-zinc-400 mt-1.5 italic px-1">
          Read-only Übersicht. Aktionen werden vom Back-Office ausgelöst.
        </div>
      </div>
    </div>
  );
}
